import {
    DEFAULT_PERMISSIONS as APP_DEFAULT_PERMISSIONS,
    PERMISSIONS as APP_PERMISSIONS,
} from '@/config/permissions.config.ts'
import {
    DEFAULT_ROUTE_PRIORITY,
    ROUTE_PERMISSIONS,
} from '@/config/routes.config.ts'
import type { PermissionKey } from '@/shared/Types/config.types.ts'

export {
    DEFAULT_ADMIN_PERMISSION_KEYS,
    DEFAULT_PERMISSIONS,
    PERMISSIONS,
} from '@/config/permissions.config.ts'
export {
    DEFAULT_ROUTE_PRIORITY,
    ROUTE_PERMISSIONS,
} from '@/config/routes.config.ts'
export type {
    PermissionDefinition,
    PermissionKey,
} from '@/shared/Types/config.types.ts'

export const SUPER_USER_PERMISSION = APP_PERMISSIONS.ADMIN_SUPER_USER
const CONFIGURED_PERMISSION_KEYS = new Set<string>(
    APP_DEFAULT_PERMISSIONS.map((permission) => permission.key),
)

export function isConfiguredPermission(
    permission: string,
): permission is PermissionKey {
    return CONFIGURED_PERMISSION_KEYS.has(permission)
}

export function getConfiguredPermissionKeys(permissions: Array<string>) {
    return permissions.filter(isConfiguredPermission)
}

export function hasPermission(
    permissions: Array<string> | undefined,
    permission: PermissionKey,
) {
    return Boolean(permissions?.includes(permission))
}

export function hasSuperUserPermission(permissions: Array<string>) {
    return hasPermission(permissions, SUPER_USER_PERMISSION)
}

export function getVisiblePermissionKeys(
    permissions: Array<string>,
    canManageSuperUsers: boolean,
) {
    const configuredPermissions = getConfiguredPermissionKeys(permissions)

    if (canManageSuperUsers) {
        return configuredPermissions
    }

    return configuredPermissions.filter(function (permission) {
        return permission !== SUPER_USER_PERMISSION
    })
}

export function getDefaultRouteForPermissions(permissions: Array<string>) {
    for (const routeConfig of DEFAULT_ROUTE_PRIORITY) {
        if (hasPermission(permissions, routeConfig.permission)) {
            return routeConfig.route
        }
    }

    return '/login'
}

export function getPermissionForRoute(pathname: string) {
    const routePermissions = ROUTE_PERMISSIONS as Record<string, PermissionKey>

    return routePermissions[normalizePathname(pathname)]
}

function normalizePathname(pathname: string) {
    const pathWithoutSearch = pathname.split('?')[0]?.split('#')[0] ?? '/'

    if (pathWithoutSearch.length > 1 && pathWithoutSearch.endsWith('/')) {
        return pathWithoutSearch.slice(0, -1)
    }

    return pathWithoutSearch
}
